// Location: src/pages/plan-summary/components/WeeklyBreakdown.tsx

import { useTranslation } from 'react-i18next';

interface WeeklyStat {
  week: number;
  adherence: number;
  weight_change: number;
  days_completed: number;
}

interface WeeklyBreakdownProps {
  weeklyStats: WeeklyStat[];
}

export default function WeeklyBreakdown({ weeklyStats }: WeeklyBreakdownProps) {
  const { t } = useTranslation();

  if (!weeklyStats || weeklyStats.length === 0) {
    return null;
  }

  const getBestWeek = () => {
    return weeklyStats.reduce((best, current) =>
      current.adherence > best.adherence ? current : best
    );
  };

  const bestWeek = getBestWeek();

  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-3xl p-6 lg:p-8 backdrop-blur-sm flex flex-col h-full">
      <div className="flex flex-wrap gap-4 items-center justify-between mb-8">
        <div>
          <h3 className="text-xl font-light text-white tracking-tight">{t('planSummary.weeklyBreakdown.title')}</h3>
          <p className="text-xs text-gray-500 mt-1">{t('planSummary.weeklyBreakdown.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-500/10 border border-yellow-500/20 rounded-full">
          <i className="ri-star-fill text-yellow-400 text-xs"></i>
          <span className="text-[10px] text-yellow-400 uppercase tracking-widest font-medium">
            {t('planSummary.weeklyBreakdown.bestWeek', {
              week: bestWeek.week,
              adherence: bestWeek.adherence
            })}
          </span>
        </div>
      </div>

      <div className="space-y-6 flex-grow">
        {weeklyStats.map((week) => {
          const isBest = week.week === bestWeek.week;

          return (
            <div
              key={week.week}
              className={`relative p-5 rounded-2xl border transition-all duration-300 ${
                isBest
                  ? 'bg-yellow-500/[0.03] border-yellow-500/20 shadow-[0_0_15px_rgba(250,204,21,0.05)]'
                  : 'bg-white/[0.01] border-white/5 hover:bg-white/[0.03]'
              }`}
            >
              {isBest && (
                <div className="absolute top-0 right-0 p-2 opacity-50">
                  <i className="ri-vip-crown-2-fill text-yellow-500 text-6xl blur-xl absolute -top-4 -right-4"></i>
                </div>
              )}
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      isBest ? 'bg-yellow-500/20 text-yellow-400' : 'bg-white/5 text-gray-400'
                    }`}>
                      W{week.week}
                    </div>
                    <div>
                      <div className="text-white text-sm font-medium">
                        {t('planSummary.weeklyBreakdown.week', { number: week.week })}
                      </div>
                      {!!week.days_completed && (
                        <div className="text-gray-500 text-xs mt-0.5">
                          {t('planSummary.weeklyBreakdown.daysCompleted', { count: week.days_completed })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={`text-sm font-light tracking-tight ${
                    week.weight_change < 0 ? 'text-green-400' : week.weight_change > 0 ? 'text-blue-400' : 'text-gray-400'
                  }`}>
                    {week.weight_change > 0 ? '+' : ''}{week.weight_change.toFixed(1)} <span className="text-[10px] opacity-60">kg</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-gray-500 uppercase tracking-widest">{t('planSummary.weeklyBreakdown.adherence')}</span>
                    <span className="text-sm text-white font-medium">{week.adherence}%</span>
                  </div>
                  <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all duration-1000 ${
                        week.adherence >= 90 ? 'bg-green-500 shadow-[0_0_10px_rgba(34,197,94,0.5)]'
                        : week.adherence >= 70 ? 'bg-yellow-400 shadow-[0_0_10px_rgba(250,204,21,0.5)]'
                        : 'bg-orange-500 shadow-[0_0_10px_rgba(249,115,22,0.5)]'
                      }`}
                      style={{ width: `${week.adherence}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 pt-6 border-t border-white/5 grid grid-cols-3 gap-4">
        <div>
          <div className="text-gray-500 text-[10px] uppercase tracking-widest mb-1">{t('planSummary.weeklyBreakdown.summary.avgAdherence')}</div>
          <div className="text-white font-light text-xl">
            {Math.round(weeklyStats.reduce((sum, w) => sum + w.adherence, 0) / weeklyStats.length)}<span className="text-xs text-gray-500">%</span>
          </div>
        </div>
        <div>
          <div className="text-gray-500 text-[10px] uppercase tracking-widest mb-1">{t('planSummary.weeklyBreakdown.summary.totalWeightChange')}</div>
          <div className="text-white font-light text-xl">
            {weeklyStats.reduce((sum, w) => sum + w.weight_change, 0).toFixed(1)} <span className="text-xs text-gray-500">kg</span>
          </div>
        </div>
        <div>
          <div className="text-gray-500 text-[10px] uppercase tracking-widest mb-1">{t('planSummary.weeklyBreakdown.summary.weeksCompleted')}</div>
          <div className="text-white font-light text-xl">
            {weeklyStats.length} <span className="text-xs text-gray-500">wks</span>
          </div>
        </div>
      </div>
    </div>
  );
}